import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useViewMode } from '../../context/ViewModeContext';
import {
  Users,
  Package,
  FileText,
  Receipt,
  UserCog,
  Settings,
  Menu,
  ChevronLeft,
  LogOut,
  LayoutDashboard,
  Smartphone
} from 'lucide-react';
import { Button } from '../ui/button';
import api from '../../utils/api';

export const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const { user, logout, hasRole } = useAuth();
  const { switchToMobile } = useViewMode();
  const navigate = useNavigate();

  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const response = await api.getCompanySettings();
        if (response.data?.company_name) {
          setCompanyName(response.data.company_name);
        }
      } catch (error) {
        console.error('Failed to load company name:', error);
      }
    };
    loadCompanyName();
  }, []);

  const handleSwitchToMobile = () => {
    switchToMobile();
    navigate('/m');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/app/dashboard', roles: ['ADMIN', 'MANAGER', 'SECRETARY', 'EMPLOYEE'] },
    { icon: Users, label: 'Clienti', path: '/app/customers', roles: ['ADMIN', 'MANAGER', 'SECRETARY', 'EMPLOYEE'] },
    { icon: Package, label: 'Articoli', path: '/app/items', roles: ['ADMIN', 'MANAGER', 'SECRETARY', 'EMPLOYEE'] },
    { icon: FileText, label: 'Bollettini', path: '/app/work-orders', roles: ['ADMIN', 'MANAGER', 'SECRETARY', 'EMPLOYEE'] },
    { icon: Receipt, label: 'Fatture', path: '/app/invoices', roles: ['ADMIN', 'MANAGER', 'SECRETARY'] },
    { icon: UserCog, label: 'Personale', path: '/app/personnel', roles: ['ADMIN', 'MANAGER'] },
    { icon: Settings, label: 'Impostazioni', path: '/app/settings', roles: ['ADMIN'] },
  ];

  const visibleItems = menuItems.filter(item => hasRole(item.roles));

  return (
    <div
      className={`bg-slate-900 text-slate-300 h-screen flex flex-col transition-all duration-300 ${
        collapsed ? 'w-16' : 'w-64'
      }`}
      data-testid="sidebar"
    >
      {/* Header */}
      <div className="p-4 border-b border-slate-700 flex items-center justify-between">
        {!collapsed && (
          <div>
            <h1 className="font-heading text-white text-xl font-bold">BollettinoX</h1>
            {companyName && (
              <p className="text-xs text-slate-400 mt-0.5">{companyName}</p>
            )}
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!collapsed)}
          className="text-slate-300 hover:text-white hover:bg-slate-800"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <Menu size={20} /> : <ChevronLeft size={20} />}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 overflow-y-auto">
        {visibleItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded mb-1 transition-colors ${
                  isActive
                    ? 'bg-primary text-white'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`
              }
              data-testid={`nav-${item.path.slice(1)}`}
            >
              <Icon size={20} />
              {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* User Info & Actions */}
      <div className="p-4 border-t border-slate-700">
        {!collapsed && user && (
          <div className="mb-3">
            <p className="text-sm font-medium text-white">
              {user.first_name} {user.last_name}
            </p>
            <p className="text-xs text-slate-400">{user.email}</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {user.role === 'ADMIN' && 'Amministratore'}
              {user.role === 'MANAGER' && 'Responsabile'}
              {user.role === 'SECRETARY' && 'Segretaria'}
              {user.role === 'EMPLOYEE' && 'Impiegato'}
            </p>
          </div>
        )}
        {/* Versione Mobile */}
        <Button
          variant="ghost"
          size={collapsed ? 'icon' : 'sm'}
          onClick={handleSwitchToMobile}
          className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800 mb-1"
          data-testid="switch-to-mobile-btn"
        >
          <Smartphone size={18} />
          {!collapsed && <span className="ml-2">Versione Mobile</span>}
        </Button>
        {/* Esci */}
        <Button
          variant="ghost"
          size={collapsed ? 'icon' : 'sm'}
          onClick={logout}
          className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
          data-testid="logout-button"
        >
          <LogOut size={18} />
          {!collapsed && <span className="ml-2">Esci</span>}
        </Button>
        {/* Legal footer */}
        {!collapsed && (
          <div className="pt-3 mt-1 border-t border-slate-800 text-[10px] text-slate-500 leading-tight" data-testid="sidebar-legal-footer">
            <a href="/privacy" className="hover:text-slate-300" target="_blank" rel="noopener noreferrer">Privacy</a>
            <span className="mx-1">·</span>
            <a href="/impressum" className="hover:text-slate-300" target="_blank" rel="noopener noreferrer">Impressum</a>
            <span className="mx-1">·</span>
            <span>BETA</span>
          </div>
        )}
      </div>
    </div>
  );
};
